import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';

export type ScreenState = 'full' | 'hidden';

export interface RoleModel {
    id: number;
    name: string;
    display_name: string;
    description: string;
    activated: number;
    icon: string | null;
}

export interface RolePermissions {
    [screenKey: string]: ScreenState;
}

/**
 * Entry do batch de permissões de um role — CONTRATO do backend
 * (BatchUpdateRolePermissionsRequest): o payload de
 * PUT /screen-permissions/role/{role}/batch é uma LISTA de
 * `{ screen_key, state }`, NÃO um mapa.
 */
export interface RolePermissionEntry {
    screen_key: string;
    state: ScreenState;
}

export interface AllPermissions {
    by_role: { [roleName: string]: RolePermissions };
    by_user: { [userId: number]: RolePermissions };
}

export interface CreateRolePayload {
    name: string;
    display_name: string;
    description?: string;
    icon?: string;
}

export interface UpdateRolePayload {
    display_name?: string;
    description?: string;
    activated?: boolean;
    icon?: string;
}

@Injectable({
    providedIn: 'root',
})
export class AccessControlService {
    private readonly base = `${environment.config.apiUrl}/v1`;

    constructor(private _http: HttpClient) {}

    // ── Roles ────────────────────────────────────────────────────────────

    getRoles(): Observable<{ roles: RoleModel[] }> {
        return this._http.get<{ roles: RoleModel[] }>(`${this.base}/roles`);
    }

    createRole(payload: CreateRolePayload): Observable<{ message: string; role: RoleModel }> {
        return this._http.post<{ message: string; role: RoleModel }>(`${this.base}/roles`, payload);
    }

    updateRole(id: number, payload: UpdateRolePayload): Observable<{ message: string; role: RoleModel }> {
        return this._http.put<{ message: string; role: RoleModel }>(`${this.base}/roles/${id}`, payload);
    }

    deleteRole(id: number): Observable<{ message: string }> {
        return this._http.delete<{ message: string }>(`${this.base}/roles/${id}`);
    }

    checkRoleUsers(roleId: number): Observable<{ can_delete: boolean; user_count: number }> {
        return this._http.get<{ can_delete: boolean; user_count: number }>(`${this.base}/roles/${roleId}/check-users`);
    }

    // ── Screen Permissions ───────────────────────────────────────────────

    getAllPermissions(): Observable<AllPermissions> {
        return this._http.get<AllPermissions>(`${this.base}/screen-permissions`);
    }

    getPermissionsByRole(roleName: string): Observable<{ role: string; permissions: RolePermissions }> {
        return this._http.get<{ role: string; permissions: RolePermissions }>(
            `${this.base}/screen-permissions/role/${roleName}`
        );
    }

    updateRolePermission(roleName: string, screenKey: string, state: ScreenState): Observable<any> {
        return this._http.put(`${this.base}/screen-permissions/role/${roleName}/${screenKey}`, { state });
    }

    /**
     * Atualiza o toggle "Concessão de Contexto" de um role×módulo.
     * granted=false  → desliga (cria exceção OFF).
     * granted=true   → liga (remove a exceção, volta ao default ON implícito).
     */
    updateRoleContextGrant(roleName: string, contextScreenKey: string, granted: boolean): Observable<any> {
        return this._http.put(`${this.base}/screen-permissions/role/${roleName}/context/${contextScreenKey}`, { granted });
    }

    /**
     * Salva um LOTE de permissões do role de uma vez.
     * @param permissions Lista de { screen_key, state } — contrato do backend
     *                    (BatchUpdateRolePermissionsRequest). NUNCA enviar mapa.
     */
    updateRolePermissionsBatch(roleName: string, permissions: RolePermissionEntry[]): Observable<any> {
        return this._http.put(`${this.base}/screen-permissions/role/${roleName}/batch`, { permissions });
    }

    getPermissionsByUser(userId: number): Observable<{ user_id: number; permissions: RolePermissions }> {
        return this._http.get<{ user_id: number; permissions: RolePermissions }>(
            `${this.base}/screen-permissions/user/${userId}`
        );
    }

    updateUserPermission(userId: number, screenKey: string, state: ScreenState): Observable<any> {
        return this._http.put(`${this.base}/screen-permissions/user/${userId}/${screenKey}`, { state });
    }

    deleteUserPermission(userId: number, screenKey: string): Observable<any> {
        return this._http.delete(`${this.base}/screen-permissions/user/${userId}/${screenKey}`);
    }

    getMyPermissions(): Observable<{ role: string; permissions: RolePermissions }> {
        return this._http.get<{ role: string; permissions: RolePermissions }>(
            `${this.base}/screen-permissions/me`
        );
    }
}
